import { useState, useRef, useEffect } from 'react';

interface PinInputProps {
  length?: number;
  onComplete: (pin: string) => void;
  onChange?: (pin: string) => void;
  error?: boolean;
  disabled?: boolean;
}

export default function PinInput({
  length = 6,
  onComplete,
  onChange,
  error = false,
  disabled = false,
}: PinInputProps) {
  const [pin, setPin] = useState<string[]>(new Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Focus first input on mount
    if (!disabled && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [disabled]);

  const handleChange = (index: number, value: string) => {
    if (disabled) return;

    // Allow only numbers
    if (!/^\d*$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value.slice(-1); // Take only the last character entered
    setPin(newPin);

    const pinString = newPin.join('');
    if (onChange) onChange(pinString);

    // Auto-focus next input
    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Trigger complete if full
    if (pinString.length === length && !newPin.includes('')) {
      onComplete(pinString);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (disabled) return;

    const pastedData = e.clipboardData.getData('text').slice(0, length).replace(/\D/g, '');
    if (!pastedData) return;

    const newPin = [...pin];
    for (let i = 0; i < pastedData.length; i++) {
      newPin[i] = pastedData[i];
    }
    setPin(newPin);

    const pinString = newPin.join('');
    if (onChange) onChange(pinString);
    if (pinString.length === length) {
      onComplete(pinString);
      inputRefs.current[length - 1]?.focus();
    } else {
      inputRefs.current[pastedData.length]?.focus();
    }
  };

  return (
    <div className="flex gap-2 justify-center">
      {pin.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="password"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          disabled={disabled}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          data-bwignore="true"
          data-lpignore="true"
          name={`pin-digit-${index}`}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          className={`w-10 h-12 md:w-12 md:h-14 text-center text-xl font-bold border-2 rounded-lg outline-none transition-all duration-200
            ${
              error
                ? 'border-red-500 bg-red-50 text-red-600 focus:border-red-600 focus:ring-2 focus:ring-red-200'
                : 'border-gray-300 bg-white text-gray-900 focus:border-[#04BFBF] focus:ring-2 focus:ring-[#04BFBF]/20'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}
          `}
        />
      ))}
    </div>
  );
}
